import {
  Component,
  OnDestroy,
  OnInit,
  viewChild,
  inject,
  WritableSignal,
  signal,
  model,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api';
import { emptyNote } from '../../../utils/utils';
import { AuthService } from 'src/app/services/auth.service';
import {
  ControllerNote,
  ControllerNoteCategory,
} from 'src/app/model/controllerNotes/controllerNote';
import { TabService } from 'src/app/core/services/tab.service';
import { ControllerNotesApiService } from 'src/app/services/api/controller-notes-api.service';
import { AorSelectPanelComponent } from '../controller-notes/aor-select-panel/aor-select-panel.component';
import { AOR_VIEW_CONTAINER } from 'src/app/constants/common.constants';
import { NoteFormComponent } from '../controller-notes/note-form/note-form.component';
import { firstValueFrom, interval, Subscription } from 'rxjs';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { MultiSelectChangeEvent } from '@openng/optimus-ui/multiselect';
import { CLAIMS } from 'src/app/constants/auth-constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import {
  DialogService,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { CONTROLLER_NOTES_SEARCH_TABLE_DEFINITION } from '../../definitions/controller-notes-search-table-definition';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { ControllerNotesBuilderService } from 'src/app/services/builder/controller-notes-builder.service';
import { AorBuilderService } from 'src/app/services/builder/aor-builder.service';
import { ControllerNoteTableService } from 'src/app/services/table/controller-note-table.service';
import {
  NoteFormInput,
  SharedData,
} from 'src/app/model/controllerNotes/sharedData';
import { AOR } from 'src/app/model/locations/aor';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { FormsModule } from '@angular/forms';
import { ToggleSwitch } from '@openng/optimus-ui/toggleswitch';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Toast } from '@openng/optimus-ui/toast';
import { ConfirmDialog } from '@openng/optimus-ui/confirmdialog';
import { TooltipModule } from '@openng/optimus-ui/tooltip';

@Component({
  selector: 'app-controller-notes-container',
  templateUrl: './controller-notes-container.component.html',
  styleUrls: ['./controller-notes-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DatePicker,
    FormsModule,
    AorSelectPanelComponent,
    BusinessUnitFilterComponent,
    ToggleSwitch,
    ButtonDirective,
    GeneralGridComponent,
    Toast,
    ConfirmDialog,
    TranslatePipe,
    TooltipModule,
  ],
})
export class ControllerNotesContainerComponent
  extends SearchContainerComponent<ControllerNote>
  implements OnInit, OnDestroy
{
  protected _controllerNotesApi = inject(ControllerNotesApiService);
  protected _translateService = inject(TranslateService);
  protected _dialogService = inject(DialogService);
  protected _tableService = inject(ControllerNoteTableService);
  protected _auth = inject(AuthService);
  protected _confirmationService = inject(ConfirmationService);
  protected _messageService = inject(MessageService);
  protected _authApi = inject(AuthApiService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _controllerNotesBuilder = inject(ControllerNotesBuilderService);
  protected _aorBuilder = inject(AorBuilderService);
  protected _tabService = inject(TabService);

  readonly aorSelector = viewChild.required<AorSelectPanelComponent>('aorSel');
  readonly noteFormChild = viewChild.required<NoteFormComponent>('noteForm');
  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');

  readonly controllerNotes: WritableSignal<ControllerNote[]> = signal([]);
  readonly selectedBusinessUnits: WritableSignal<string[]> = signal([]);
  ref: DynamicDialogRef<NoteFormComponent> | null = null;

  selectedNote: ControllerNote = { ...emptyNote };
  dialogVisible: boolean = false;
  isLoading: boolean = true;
  emailInputLoading: boolean = false;
  emailInputVisible: boolean = false;
  formType: string = 'Edit';
  formLoading: boolean = false;
  formVisible: boolean = false;
  category: ControllerNoteCategory = 'M&R';
  movingToEditForm: boolean = false;
  viewLoading: boolean = false;
  canDelete = false;
  canDeleteOwn = false;
  canEdit = false;
  canEditOwn = false;
  canViewMusterList = false;
  hideMaintenance: boolean = false;
  startDate!: Date;
  endDate!: Date;

  tableVals!: ControllerNote[];
  isAorView: boolean = false;
  refreshIntervalLoading: boolean = false;

  trulyEmptyNote = { ...emptyNote };

  refreshSubscription?: Subscription;
  readonly aors = model<AOR[]>([]);
  readonly data = model<SharedData>({
    selectedAors: [],
    totalAors: [],
    businessUnits: [],
    startDate: this.startDate,
    endDate: this.endDate,
  });

  constructor() {
    super();
    this.setToDefaultDate();
  }

  async ngOnInit() {
    this.selectedBusinessUnits.set(
      this._authApi.getUserBusinessUnits().map((bu) => bu.id)
    );

    const tabLabel = await firstValueFrom(
      this._translateService.get('CRM.LABEL.CONTROLLER_NOTES')
    );
    this._tabService.updateActiveTabLabel(tabLabel);

    this._tableService.setLastLazyLoad(null);
    this.setTableService(this._tableService);
    this.columns = CONTROLLER_NOTES_SEARCH_TABLE_DEFINITION;

    const refreshInterval = interval(60000);
    this.refreshSubscription = refreshInterval.subscribe(() => {
      this.refreshIntervalLoading = true;
      this.refresh();
    });

    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.EDIT_CONTROLLER_NOTES,
    ]);
    this.canEditOwn = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.EDIT_OWN_CONTROLLER_NOTES,
    ]);
    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.DELETE_CONTROLLER_NOTES,
    ]);
    this.canDeleteOwn = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.DELETE_OWN_CONTROLLER_NOTES,
    ]);
    this.canViewMusterList = this._authApi.doesUserHaveClaim(
      CLAIMS.FIELD_OPERATIONS.MUSTER_LIST.VIEW_MUSTER_LIST
    );

    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  setToDefaultDate(): void {
    // Default date range to today at 5:45am to tomorrow at 5:45am
    const today = new Date();
    today.setHours(5, 45, 0, 0);
    this.startDate = today;
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.endDate = tomorrow;
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
    // Reset to default date when component is destroyed
    this.setToDefaultDate();
  }

  gotoAORView(event: MultiSelectChangeEvent) {
    const aor = event.value[0];
    let selectedAor = this._aorBuilder.buildAor(event.value[0]);
    // this.setToDefaultDate();
    this.buildSharedModel();
    this.data.update((data) => ({
      ...this.data(),
      selectedAors: [selectedAor],
    }));
    // send the user to the aor view with their selected aor and shift date
    const newTab = this._tabService.buildNewAORTab(
      AOR_VIEW_CONTAINER,
      true,
      `${aor.name} - ${this.startDate?.toLocaleDateString()}`,
      undefined,
      undefined,
      this.data()
    );
    this._tabService.openTab(newTab, true);
    setTimeout(() => {
      this.aorSelector().clear();
    }, 100);
  }

  buildSharedModel(): void {
    this.data.set({
      selectedAors: [],
      totalAors: this.aors(),
      businessUnits: this.selectedBusinessUnits(),
      startDate: this.startDate,
      endDate: this.endDate,
    });
  }

  onDialogHide() {
    if (!this.movingToEditForm) {
      this.resetSelectedNote();
    } else {
      this.movingToEditForm = false;
    }
    this.dialogVisible = false;
    this.refresh();
  }

  getBusinessUnits(data: any) {
    this.selectedBusinessUnits.set(data);
    // need to delay this a tiny amount so that the selected business units update before we query
    setTimeout(() => {
      this.aorSelector().populateOptions();
    });
    this.refresh();
  }

  onDateChange($event: Date, isStartDate: boolean): void {
    const updatedDate = $event;
    updatedDate.setHours(5, 45, 0, 0);
    if (isStartDate) {
      this.startDate = updatedDate;
    } else {
      this.endDate = updatedDate;
    }

    if (this.startDate >= this.endDate) {
      const date = new Date();
      date.setFullYear(this.startDate.getFullYear());
      date.setMonth(this.startDate.getMonth());
      date.setDate(this.startDate.getDate() + 1);
      date.setHours(5, 45, 0, 0);
      this.endDate = date;
    }

    this.refresh();
  }

  onEntryUpdated($event: ControllerNote) {
    let oldIndex = this.tableVals.findIndex((x) => x.id === $event.id);
    this.tableVals.splice(oldIndex, 1, $event);
  }

  onFormHide() {
    this.resetSelectedNote();
    this.refresh(); // ensure the notes in the table are up to date with any entries or other changes
    this.formVisible = false;
  }

  resetSelectedNote() {
    // Need to clone the empty note here instead of directly assigning,
    // otherwise we sometimes change values in the empty note
    // and accidentally prevent things like note view from opening
    this.selectedNote = { ...emptyNote };
  }

  handleBusinessUnitChange($event: any[]) {
    this.getBusinessUnits(this.businessUnitFilter()?.selectedBusinessUnits);
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    this._tableService.setLastLazyLoad($event.lazy);

    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    filters['businessUnits'] = selectedBusinessUnits as unknown as any;

    const query = this._controllerNotesBuilder.buildControllerNotesQuery(
      filters,
      this.startDate,
      this.endDate,
      this.hideMaintenance
    );
    this.queryNetwork(req, $event, query, false);
  }

  queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any },
    isAorView: boolean
  ): void {
    // For a better UX, we want to show the loading symbol when a user refreshes
    // but we do not want to show the spinner during the refresh intervals.
    if (!this.refreshIntervalLoading) {
      this.isLoading = true;
    }

    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'updatedAt', 'DESC');
    const finalQuery = { ...query };
    this.isAorView = isAorView;
    this._controllerNotesApi
      .queryForControllerNotes(
        req.page,
        req.pageSize,
        finalQuery,
        sort,
        isAorView
      )
      .subscribe({
        next: (response) => {
          this.controllerNotes.set(response.items);
          this.totalRecords = response.totalRecords;
          this.elements = [...this.controllerNotes()];
          this.refreshIntervalLoading
            ? (this.refreshIntervalLoading = false)
            : (this.isLoading = false);
        },
        error: (error) => {
          this.refreshIntervalLoading
            ? (this.refreshIntervalLoading = false)
            : (this.isLoading = false);
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'CRM.MESSAGES.ERROR.QUERY_CONTROLLER_NOTES',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
      });
  }

  changeViewEvent($event: { data: ControllerNote }): void {
    const controllerNote = $event.data;

    if (this.data()?.totalAors.length == 0) {
      this.buildSharedModel();
    }
    const noteFormInput: NoteFormInput = {
      ...this.data(),
      controllerNote: $event.data,
      category: controllerNote?.category!,
      isAorView: this.isAorView,
      formType: 'Edit',
    };

    this.openControllerNoteDialog(noteFormInput);
  }

  async openControllerNoteDialog(noteFormInput: NoteFormInput): Promise<void> {
    const dialogHeader = await firstValueFrom(
      this._translateService.get('CRM.MESSAGES.HEADERS.NOTE_INFORMATION')
    );

    this.ref = this._dialogService.open(NoteFormComponent, {
      header: dialogHeader,
      dismissableMask: true,
      modal: true,
      closable: true,
      width: '80%',
      data: {
        input: noteFormInput,
      },
    });
    this.ref?.onClose.subscribe((data) => {
      if (!data) {
        // If no data returns, we still refresh in case entries were updated but we do not want to display the spinner.
        this.refreshIntervalLoading = true;
      }
      this.refresh();
    });
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._tableService.getLastLazyLoad()! });
  }

  override clearScreen() {
    this.grid()?.resetTable();
    this.elements = [];
    this.hideMaintenance = false;
  }
}
